// Heatmaps — ported from the umag.html "A képlet viselkedése egyforma
// képességű játékosoknál" visualization. Uniform-skill players (all skills =
// level) on a fixed 1–5 scale, no bonuses. Four small SVGs: each player's
// serve game, the opponent's serve game, and the full-match total.
import { fmt } from './calc';

const N = 5;

type Matrix = number[][]; // matrix[level-1][level-1], level 1..N

function buildMatrices(): { title: string; mat: Matrix }[] {
  const matFirst: Matrix = [];
  const matSecond: Matrix = [];
  const matOpp: Matrix = [];
  const matTotal: Matrix = [];
  for (let i = 1; i <= N; i++) {
    const rowF: number[] = [];
    const rowS: number[] = [];
    const rowO: number[] = [];
    const rowT: number[] = [];
    for (let j = 1; j <= N; j++) {
      const n1 = i / N;
      const n2 = j / N;
      const f = (n1 + n1 + 0.5) * (n2 + 1);
      const s = (n2 + n2 + 0.5) * (n1 + 1);
      const oPerGame = (n1 + 1) * (n2 + 1);
      rowF.push(f);
      rowS.push(s);
      rowO.push(oPerGame);
      rowT.push(f + s + 2 * oPerGame);
    }
    matFirst.push(rowF);
    matSecond.push(rowS);
    matOpp.push(rowO);
    matTotal.push(rowT);
  }
  return [
    { title: 'Az 1. tag szervája alatt', mat: matFirst },
    { title: 'A 2. tag szervája alatt', mat: matSecond },
    { title: 'Az ellenfél szervája alatt', mat: matOpp },
    { title: 'Összesen', mat: matTotal },
  ];
}

const STOPS: [number[], number[]] = [
  [226, 234, 214], // c-green-tint
  [29, 74, 51], // c-green
];

function Heatmap({ title, mat }: { title: string; mat: Matrix }) {
  let lo = Infinity;
  let hi = -Infinity;
  for (const row of mat)
    for (const v of row) {
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
  const colorAt = (v: number) => {
    const t = (v - lo) / (hi - lo || 1);
    const r = Math.round(STOPS[0][0] + (STOPS[1][0] - STOPS[0][0]) * t);
    const g = Math.round(STOPS[0][1] + (STOPS[1][1] - STOPS[0][1]) * t);
    const b = Math.round(STOPS[0][2] + (STOPS[1][2] - STOPS[0][2]) * t);
    return `rgb(${r}, ${g}, ${b})`;
  };
  const textFor = (v: number) => {
    const t = (v - lo) / (hi - lo || 1);
    return t > 0.55 ? 'rgb(245, 239, 222)' : 'rgb(19, 51, 36)';
  };

  const PAD_L = 44;
  const PAD_B = 30;
  const PAD_T = 8;
  const PAD_R = 8;
  const CELL = 38;
  const W = PAD_L + N * CELL + PAD_R;
  const H = PAD_T + N * CELL + PAD_B;

  const cells = [];
  for (let i = 0; i < N; i++) {
    for (let j = 0; j < N; j++) {
      const rowVal = N - i; // 5 at top
      const colVal = j + 1;
      const v = mat[rowVal - 1][colVal - 1];
      const x = PAD_L + j * CELL;
      const y = PAD_T + i * CELL;
      cells.push(
        <g key={`${i}-${j}`}>
          <rect
            className="heatmap-cell"
            x={x}
            y={y}
            width={CELL}
            height={CELL}
            fill={colorAt(v)}
          >
            <title>{`${rowVal}–${colVal}: ${fmt(v, 2)}`}</title>
          </rect>
          <text
            className="heatmap-value"
            x={x + CELL / 2}
            y={y + CELL / 2 + 4}
            textAnchor="middle"
            fill={textFor(v)}
          >
            {fmt(v, 1)}
          </text>
        </g>,
      );
    }
  }

  const yTicks = [];
  for (let i = 0; i < N; i++) {
    yTicks.push(
      <text
        key={`y${i}`}
        className="heatmap-axis"
        x={PAD_L - 6}
        y={PAD_T + i * CELL + CELL / 2 + 4}
        textAnchor="end"
      >
        {N - i}
      </text>,
    );
  }
  const xTicks = [];
  for (let j = 0; j < N; j++) {
    xTicks.push(
      <text
        key={`x${j}`}
        className="heatmap-axis"
        x={PAD_L + j * CELL + CELL / 2}
        y={PAD_T + N * CELL + 14}
        textAnchor="middle"
      >
        {j + 1}
      </text>,
    );
  }

  const yLabelCx = 12;
  const yLabelCy = PAD_T + (N * CELL) / 2;

  return (
    <div className="heatmap">
      <p className="heatmap-title">{title}</p>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={title}>
        {cells}
        {yTicks}
        {xTicks}
        <text
          className="heatmap-axis-label"
          x={yLabelCx}
          y={yLabelCy}
          textAnchor="middle"
          dominantBaseline="middle"
          transform={`rotate(-90, ${yLabelCx}, ${yLabelCy})`}
        >
          1. játékos
        </text>
        <text
          className="heatmap-axis-label"
          x={PAD_L + (N * CELL) / 2}
          y={H - 4}
          textAnchor="middle"
        >
          2. játékos
        </text>
      </svg>
    </div>
  );
}

export default function CikkHeatmaps() {
  const maps = buildMatrices();
  return (
    <div className="interactive">
      <p className="interactive-label">Vizualizáció</p>
      <h4>A képlet viselkedése egyforma képességű játékosoknál</h4>
      <p className="intro">
        Az alábbi hőtérképek azt mutatják, milyen értéket ad a képlet egyes tagja, ha
        mindkét csapattag minden képessége egyforma (1–5 skálán). A sötétebb zöld erősebb
        párost jelöl. Bónuszok nélkül.
      </p>

      <div className="heatmaps" aria-label="Páros erőssége hőtérképek">
        {maps.map((m) => (
          <Heatmap key={m.title} title={m.title} mat={m.mat} />
        ))}
      </div>

      <p
        style={{
          marginTop: '1rem',
          fontSize: '0.92rem',
          color: 'var(--c-text-soft)',
        }}
      >
        A hőtérképekből leolvasható, hogy két erős játékos saját szervája alatt a
        legerősebb (5–5 cella, bal felső térkép), illetve hogy gyenge játékosoknál inkább
        az ellenfél szervája alatt teljesít jobban a páros (1–1 cella). Az átló mentén
        minden szimmetrikus, hiszen az 5–5 vagy 3–3 párosban mindegy, melyik játékos
        tagját nevezzük az „elsőnek”.
      </p>
    </div>
  );
}
